import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { debounceTime } from "rxjs/operators";
import eventEmitter from "../../eventEmitter";
import Card from "../Card";
import { groupCards as groupCardsGene } from "../geneCards";
import { groupCards as groupCardsVariant } from "../cards";
import CardGridEl from "../CardWrapper/CardGridEl";
import { getQueryType } from "../../../state/selectors/general.selectors";

const GroupedCards = React.memo(({ cards, queryType, isCollapsed }) => {
  const containerRef = useRef(null);
  const cardWidth = 220;
  const [grid, setGrid] = useState([[]]);

  function splitArrTailRec(arr, ind, result = []) {
    if (arr.length <= ind) {
      return [...result, arr];
    }
    return splitArrTailRec(arr.slice(ind), ind, [...result, arr.slice(0, ind)]);
  }

  function calculateColumns(arr, rows) {
    return Math.ceil(arr.length / rows);
  }

  function findOptimalGrid(allCards, rows, columns, categories) {
    const gridCells = rows * columns;
    const requiredColumns = categories
      .map((items) => calculateColumns(items, rows))
      .reduce((a, i) => a + i, 1);

    if (gridCells < allCards) {
      return findOptimalGrid(allCards, rows + 1, columns, categories);
    }

    if (requiredColumns > columns) {
      return findOptimalGrid(allCards, rows + 1, columns, categories);
    }

    return { columns, rows };
  }

  function adjustGrid() {
    const windowWidth = containerRef.current.offsetWidth;
    const bestColumnFit = Math.floor(windowWidth / cardWidth);
    const isGene = queryType === "gene";
    const groupCards = isGene ? groupCardsGene : groupCardsVariant;
    const { weakCards, strongColumns } = groupCards(cards, bestColumnFit);
    const initColumns = Math.max(strongColumns.length + 1, bestColumnFit);
    const initRows = Math.floor(cards.length / initColumns);

    const { rows, columns } = findOptimalGrid(
      cards.length,
      initRows,
      initColumns,
      strongColumns
    );

    const assignedStrong = strongColumns
      .map((x) => splitArrTailRec(x, rows))
      .reduce((a, i) => [...a, ...i], [])
      .filter((x) => x.length);

    const cardGrid = [[], ...assignedStrong];
    const remainingColumnLength = columns - cardGrid.length;

    for (let i = 0; i < remainingColumnLength; i++) {
      cardGrid.push([]);
    }

    cardGrid.forEach((column) => {
      const remainingSlots = rows - column.length;
      for (let i = 0; i < remainingSlots; i++) {
        const item = weakCards.shift();
        if (item) {
          column.push(item);
        }
      }
    });

    setGrid(cardGrid.filter((x) => x.length));
  }

  useEffect(() => {
    const sub = eventEmitter.resizeTracker$
      .pipe(debounceTime(50))
      .subscribe(() => adjustGrid());

    adjustGrid();
    return () => sub.unsubscribe();
  }, [cards]);

  return (
    <div className="tw-flex tw-text-xs tw-overflow-hidden" ref={containerRef}>
      {grid.map((c, x) => (
        <div className="tw-flex-1 tw-flex-shrink-0 tw-overflow-hidden" key={x}>
          {c.filter(Boolean).map((card, y) => {
            const { Card: CardComp, id } = card;
            return (
              <CardGridEl
                id={id}
                key={id}
                column={x}
                row={y}
                card={card}
                disabled={isCollapsed}
              >
                <CardComp card={card} />
              </CardGridEl>
            );
          })}
        </div>
      ))}
    </div>
  );
});

GroupedCards.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.instanceOf(Card)),
  queryType: PropTypes.string,
  isCollapsed: PropTypes.bool,
};

GroupedCards.defaultProps = {
  cards: [],
  queryType: "",
  isCollapsed: false,
};

const mapStateToProps = (state) => ({
  queryType: getQueryType(state),
});

export default connect(mapStateToProps)(GroupedCards);
